import type { LocationId, PortCallData } from '../../core/Core';
import type { ScenarioLocation } from '../../../lib/planningData';
import {
  Currency,
  ExternalPortCostPredictionsDocument,
  type ExternalPortCostPredictionsInput,
  type ExternalPortCostPredictionsQuery,
  type ExternalPortCostPredictionsQueryVariables,
  type ScenarioTimeline_ShipFragment,
} from '../../../graphql';
import type { ApolloClient, FetchResult } from '@apollo/client';
import type { DefaultMap } from '@seaber/common/lib/collections';
import type { ClientId } from '../../core/ClientId';
import type { CostParam, PortCallCost } from './costs';
import {
  isDischargeEventTimeData,
  isLoadEventTimeData,
  isPortCallEventTimeData,
  isTCOrderEventTimeData,
} from '../../core/TimeData';
import { sumBy } from 'lodash';
import moment from 'moment';
import { formatCurrency } from '../../../lib/format';

enum ExternalPortCostOperation {
  'DISCHARGE' = '0',
  'LOAD' = '1',
}

export type PortCostPredictionByPortCall = Map<
  PortCallData['id'],
  PortCallCost
>;

export const getExternalPortCostPredictions = async (
  portCalls: PortCallData[],
  locationById: Map<LocationId, ScenarioLocation>,
  vessel: ScenarioTimeline_ShipFragment,
  client: ApolloClient<unknown>,
  shipmentsByPortCallId: DefaultMap<
    ClientId | null,
    Set<number | ClientId | null>
  >,
): Promise<{
  portCostPredictionByPortCall: PortCostPredictionByPortCall | null;
  errors: string[];
}> => {
  const portCostPredictionByPortCall: PortCostPredictionByPortCall = new Map();
  const portCallIds: PortCallData['id'][] = [];
  const requestInputs: ExternalPortCostPredictionsInput[] = [];
  const portCallLocations: ScenarioLocation[] = [];

  if (!vessel.weightCapacity) {
    return {
      portCostPredictionByPortCall,
      errors: [
        `Port cost prediction error: DWT missing for vessel ${vessel.name}`,
      ],
    };
  }

  for (const pc of portCalls) {
    const firstTd = pc.tds[0];
    const eta = firstTd.arriveInPort;
    const location = locationById.get(pc.locationId);

    if (!location?.countryByCountryId) {
      console.warn(
        `Unable to get port call costs for port call ${(
          pc.id ?? 'null'
        ).toString()}. Location or country unavailable.`,
        location,
      );

      continue;
    }

    const loadEvents = pc.tds
      .filter(isLoadEventTimeData)
      .filter(isTCOrderEventTimeData);
    const loadedMass = Math.floor(sumBy(loadEvents, (td) => td.mass));

    const etaMoment = moment(eta).tz(location.timezone ?? 'UTC');
    const etaYear = etaMoment.year().toString();
    const etaMonth = (etaMoment.month() + 1).toString();
    /* Currently we only consider weekends as holidays */
    const etaIsHoliday = etaMoment.isoWeekday() > 5;

    if (loadedMass > 0) {
      portCallIds.push(pc.id);
      requestInputs.push({
        vessel_name: `${vessel.name}`,
        dwt: vessel.weightCapacity.toString(),
        year: etaYear,
        month: etaMonth,
        bizday: etaIsHoliday ? '0' : '1',
        cargo: loadedMass.toString(),
        port_func: ExternalPortCostOperation.LOAD,
        port_name: location.name,
      });
      portCallLocations.push(location);
    }

    const dischargeEvents = pc.tds
      .filter(isDischargeEventTimeData)
      .filter(isTCOrderEventTimeData);
    const dischargedMass = Math.floor(
      sumBy(dischargeEvents, (td) => Math.abs(td.mass)),
    );

    if (dischargedMass > 0) {
      portCallIds.push(pc.id);
      requestInputs.push({
        cargo: dischargedMass.toString(),
        vessel_name: `${vessel.name}`,
        dwt: vessel.weightCapacity.toString(),
        year: etaYear,
        month: etaMonth,
        bizday: etaIsHoliday ? '0' : '1',
        port_func: ExternalPortCostOperation.DISCHARGE,
        port_name: location.name,
      });
      portCallLocations.push(location);
    }
  }

  let externalCostsResponse:
    | FetchResult<ExternalPortCostPredictionsQuery>
    | undefined = undefined;

  if (requestInputs.length > 0) {
    externalCostsResponse = await client.query<
      ExternalPortCostPredictionsQuery,
      ExternalPortCostPredictionsQueryVariables
    >({
      query: ExternalPortCostPredictionsDocument,
      variables: { input: requestInputs },
    });
  }

  const externalCosts =
    externalCostsResponse?.data?.externalPortCostPredictions || [];

  for (let i = 0; i < portCallIds.length; i++) {
    const portCallId = portCallIds[i];
    if (!portCallId) {
      continue;
    }

    const predicted = externalCosts[i]?.predicted;
    if (!predicted) {
      continue;
    }

    const location = portCallLocations[i];

    const nextPortCallIsTheSame = portCallIds[i + 1] === portCallIds[i];
    const samePortCallPredicted = nextPortCallIsTheSame
      ? externalCosts[i + 1]?.predicted
      : undefined;
    if (nextPortCallIsTheSame) {
      i++;
    }

    const numShipments = Math.max(
      shipmentsByPortCallId.get(portCallId).size,
      1,
    );

    /* In case of multiple predictions for one port call we pick the prediction with highest total cost */
    const highestPrediction =
      predicted.total_dues >= (samePortCallPredicted?.total_dues ?? 0)
        ? predicted
        : samePortCallPredicted;

    if (highestPrediction) {
      const params: CostParam[] = [];

      /** Appends to params if value is > 0 */
      function maybeAppendParam(name: string, value: number) {
        if (value > 0) {
          params.push({
            source: 'EXTERNAL',
            name,
            value: formatCurrency(value / numShipments, 0, Currency.EUR),
          });
        }
      }

      maybeAppendParam('Fairway dues', highestPrediction.fairway_dues);
      maybeAppendParam('Harbor dues', highestPrediction.harbor_dues);
      maybeAppendParam('Mooring & unmooring', highestPrediction.mooring_dues);
      maybeAppendParam('Pilotage', highestPrediction.pilotage_dues);
      maybeAppendParam('Towage', highestPrediction.towage_dues);
      maybeAppendParam('Tonnage', highestPrediction.tonnage_dues);

      const portCallEntry = portCalls
        .find((pc) => pc.id === portCallId)
        ?.tds.find(isPortCallEventTimeData)?.event.entry;

      portCostPredictionByPortCall.set(portCallId, {
        type: 'PORT_CALL',
        name: 'Predicted port costs',
        value: highestPrediction.total_dues / numShipments,
        params: params,
        locations: location ? [location] : [],
        numShipments,
        isExternalPrediction: true,
        options: portCallEntry ? { portCallEntry } : undefined,
      });
    }
  }

  return { portCostPredictionByPortCall, errors: [] };
};
